import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { PrimeiroCadastroService } from '../../services/cadastro/primeiro-cadastro.service';
import { AtividadeEmpresaService } from '../../services/atividade-empresa.service';
import { PrimeiroCadastroDTO } from '../../models/cadastro/primeiro-cadastro.model';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { SafeStorageService } from '../../shared/safe-storage.service';
import { UsuarioService } from '../../services/usuario/usuario.service';
import { UsuarioContextoDTO } from '../../models/usuario.model';
import { PermissaoService } from '../../services/permissao/permissao.service';

import { CidadeService } from '../../services/configuracoes/cidade.service';
import { CidadeDTO, CidadeResponseDTO } from '../../models/configuracoes/cidade.model';
import { PaisDDI } from '../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../shared/data/paises-ddi.data';
import { SocialLoginService } from '../../services/auth/social-login.service';
import { ModoCadastro, SocialUserData, PrimeiroCadastroSocialDTO } from '../../models/auth/social-login.model';
import { MOEDA_OPTIONS } from '../../enums/moeda.enum';
import { filtrarRucDoEvento, isDvRucValido } from '../../shared/utils/ruc.util';
import { isEmailValido } from '../../shared/utils/email.util';

interface DropdownItem { label: string; value: string; }
// A API retorna: { enum: "ADVOGADOS", nome: "Advogados", id: 4 }
// enum = nome da constante (usado como value para enviar ao backend)
// nome = nome traduzido (usado como label para exibir ao usuario)
interface AtividadeBackend { enum: string; nome: string; id: number; }
interface CidadeOption { label: string; value: number; nomeEstado: string; }

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  providers: [MessageService]
})
export class SignupComponent implements OnInit, OnDestroy {

  // Campos do formulário
  nome = '';
  emailUsuario = '';
  telefone = '';
  atividadeDaEmpresa: string | null = null;
  senha = '';
  confirmarSenha = '';

  // NOVOS
  ruc = '';
  cidadeId: number | null = null;
  moeda: string = 'GUARANI';

  // Opcoes de moeda
  moedaOptions = MOEDA_OPTIONS;

  // DDI
  paisSelecionado: PaisDDI = PAISES_DDI[0]; // Paraguay (+595) como padrao

  // Listas
  atividades: DropdownItem[] = [];
  cidades: CidadeOption[] = [];
  carregandoCidades = false;

  // Estado de carregamento do botão cadastrar (Problema 7)
  carregandoCadastro = false;

  // Flag para exibir mensagens de erro por campo (Problema 10)
  tentouSubmeter = false;

  modoSocial: ModoCadastro = 'padrao';
  socialData: SocialUserData | null = null;
  loadingGoogleSignup = false;
  loadingAppleSignup = false;

  // Flag de erro ao carregar cidades (Problema 9)
  erroCidades = false;

  // Vinculo de indicacao (capturado dos query params ?ref= / ?token=)
  codigoIndicacao: string | null = null;
  tokenIndicacao: string | null = null;
  // Canal de origem do vinculo (?canal=; ex.: "whatsapp"). Capturado junto do
  // ref/token e enviado no cadastro; so tem efeito com vinculo por codigo (?ref=).
  canalIndicacao: string | null = null;

  // Objetos de traducao
  mensagens = {
    erro: '',
    sucesso: '',
    atencao: '',
    erro_carregar_atividades: '',
    erro_carregar_cidades: '',
    campos_obrigatorios: '',
    senhas_nao_conferem: '',
    cadastro_sucesso: '',
    erro_cadastrar: '',
    email_invalido: '',
    senha_muito_curta: '',
    nome_muito_curto: '',
    telefone_invalido: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private primeiroCadastroService: PrimeiroCadastroService,
    private atividadeEmpresaService: AtividadeEmpresaService,
    private messageService: MessageService,
    private cidadeService: CidadeService,
    private translate: TranslateService,
    private apiService: ApiService,
    private authService: AuthService,
    private socialLoginService: SocialLoginService,
    private safe: SafeStorageService,
    private usuarioService: UsuarioService,
    private permissaoService: PermissaoService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    // Detectar modo social via Router state (quando vem do login com Google)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state as { socialData?: SocialUserData };
    const historyState = isPlatformBrowser(this.platformId) ? (window?.history?.state as any) : null;
    const socialData = state?.socialData || historyState?.socialData;

    if (socialData) {
      this.modoSocial = socialData.provedor;
      this.socialData = socialData;
      this.emailUsuario = socialData.email || '';
      this.nome = socialData.nome || '';
    }

    // Captura do vinculo de indicacao via query params (SSR-safe: snapshot.queryParamMap).
    // ?token= (convite por email) e resolvido primeiro pelo backend; ?ref= (link de indicacao).
    const qp = this.route.snapshot.queryParamMap;
    this.codigoIndicacao = qp.get('ref');
    this.tokenIndicacao = qp.get('token');
    // Canal segue o mesmo ciclo de vida do ref/token: lido do snapshot da URL na
    // inicializacao (nao persiste em storage — o signup nao troca de rota entre
    // etapas; o fluxo social transita via router state, sem reiniciar o componente).
    this.canalIndicacao = qp.get('canal');

    this.carregarAtividades();
    this.carregarCidades();
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.mensagens = {
      erro: this.translate.instant('common.erro'),
      sucesso: this.translate.instant('common.sucesso'),
      atencao: this.translate.instant('signup.mensagens.atencao'),
      erro_carregar_atividades: this.translate.instant('signup.mensagens.erro_carregar_atividades'),
      erro_carregar_cidades: this.translate.instant('signup.mensagens.erro_carregar_cidades'),
      campos_obrigatorios: this.translate.instant('signup.mensagens.campos_obrigatorios'),
      senhas_nao_conferem: this.translate.instant('signup.mensagens.senhas_nao_conferem'),
      cadastro_sucesso: this.translate.instant('signup.mensagens.cadastro_sucesso'),
      erro_cadastrar: this.translate.instant('signup.mensagens.erro_cadastrar'),
      email_invalido: this.translate.instant('signup.mensagens.email_invalido'),
      senha_muito_curta: this.translate.instant('signup.mensagens.senha_muito_curta'),
      nome_muito_curto: this.translate.instant('signup.mensagens.nome_muito_curto'),
      telefone_invalido: this.translate.instant('signup.mensagens.telefone_invalido')
    };
  }

  carregarAtividades(): void {
    this.atividadeEmpresaService.listarAtividades().subscribe({
      next: (res: any) => {
        const list: AtividadeBackend[] = Array.isArray(res) ? res : (res?.content ?? []);
        this.atividades = (list || [])
          .map((item: AtividadeBackend) => ({
            label: item.nome,  // Nome traduzido para exibir
            value: item.enum   // Valor da enum para enviar ao backend
          }))
          .sort((a: DropdownItem, b: DropdownItem) => a.label.localeCompare(b.label));
      },
      error: (err: any) => {
        console.error('Erro ao listar atividades', err);
        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: this.mensagens.erro_carregar_atividades,
        });
      },
    });
  }

  carregarCidades(): void {
    this.carregandoCidades = true;
    this.erroCidades = false;
    this.cidadeService.get('/cidade', { listarTodos: true }).subscribe({
      next: (res: CidadeResponseDTO) => {
        const lista: CidadeDTO[] = res?.content ?? [];
        this.cidades = lista
          .map((c: CidadeDTO) => ({ label: c.nome, value: Number(c.id), nomeEstado: c.nomeEstado ?? '' }))
          .sort((a: CidadeOption, b: CidadeOption) => a.label.localeCompare(b.label));
        this.carregandoCidades = false;
      },
      error: (err: any) => {
        console.error('Erro ao listar cidades', err);
        this.erroCidades = true;
        this.carregandoCidades = false;
        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: this.mensagens.erro_carregar_cidades,
        });
      }
    });
  }

  /** Aviso de DV invalido. So orienta: a conta e criada mesmo assim. */
  get avisoRuc(): string | null {
    return isDvRucValido(this.ruc) === false
      ? this.translate.instant('ruc_busca.erros.dv_invalido')
      : null;
  }

  filtrarRuc(event: Event): void {
    // RUC paraguaio tem o formato "NNNNNNN-D": apenas digitos, um unico hifen
    // e um unico digito verificador apos o hifen (ex.: 8255140-5).
    // Regra centralizada em shared/utils/ruc.util.ts (reaproveitada por outras telas).
    this.ruc = filtrarRucDoEvento(event);
  }

  voltar(): void {
    this.router.navigate(['/login']);
  }

  /**
   * Verdadeiro quando o e-mail esta preenchido, porem com formato invalido.
   * Alimenta o feedback inline do template (BUG-07); fica no componente para
   * nao instanciar uma RegExp a cada ciclo de change detection.
   */
  get emailFormatoInvalido(): boolean {
    return !!this.emailUsuario && !isEmailValido(this.emailUsuario);
  }

  cadastrar(): void {
    // Setar flag para exibir erros por campo (Problema 10)
    this.tentouSubmeter = true;

    // Cadastro social (Google) — sem senha
    if (this.modoSocial !== 'padrao') {
      if (
        !this.nome ||
        !this.emailUsuario ||
        !this.telefone ||
        !this.atividadeDaEmpresa ||
        !this.ruc ||
        this.cidadeId === null
      ) {
        this.messageService.add({
          severity: 'warn',
          summary: this.mensagens.atencao,
          detail: this.mensagens.campos_obrigatorios,
        });
        return;
      }
      if (!this.validarCamposCompartilhados()) return;
      this.cadastrarSocial();
      return;
    }

    // Validação de campos obrigatórios (modo padrao)
    if (
      !this.nome ||
      !this.emailUsuario ||
      !this.telefone ||
      !this.atividadeDaEmpresa ||
      !this.senha ||
      !this.confirmarSenha ||
      !this.ruc ||
      this.cidadeId === null
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: this.mensagens.atencao,
        detail: this.mensagens.campos_obrigatorios,
      });
      return;
    }

    // Validacoes compartilhadas (nome, email, telefone)
    if (!this.validarCamposCompartilhados()) return;

    // Problema 4 - Validação de comprimento mínimo da senha (min 6 caracteres)
    if (this.senha.length < 6) {
      this.messageService.add({
        severity: 'warn',
        summary: this.mensagens.atencao,
        detail: this.mensagens.senha_muito_curta,
      });
      return;
    }

    if (this.senha !== this.confirmarSenha) {
      this.messageService.add({
        severity: 'warn',
        summary: this.mensagens.atencao,
        detail: this.mensagens.senhas_nao_conferem,
      });
      return;
    }

    // Problema 11 - DDI com optional chaining e trim para fallback seguro
    const telefoneLimpo = this.telefone.replace(/\D/g, '');
    const payload: PrimeiroCadastroDTO = {
      nome: this.nome,
      emailUsuario: this.emailUsuario.trim(),
      senha: this.senha,
      ddi: this.paisSelecionado?.ddi?.replace('+', '').trim() || '595',
      telefone: telefoneLimpo,
      atividadeDaEmpresa: this.atividadeDaEmpresa!,
      ruc: this.ruc.trim(),
      cidadeId: Number(this.cidadeId),
      moeda: this.moeda
    };

    // Vinculo de indicacao — anexa apenas quando presente (evita enviar null).
    if (this.tokenIndicacao) payload.tokenIndicacao = this.tokenIndicacao;
    if (this.codigoIndicacao) payload.codigoIndicacao = this.codigoIndicacao;
    if (this.canalIndicacao) payload.canalIndicacao = this.canalIndicacao;

    // Problema 7 - Estado de carregamento do botão
    this.carregandoCadastro = true;

    this.primeiroCadastroService.primeiroCadastro(payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.mensagens.sucesso,
          detail: this.mensagens.cadastro_sucesso,
        });

        // Login automatico apos cadastro bem-sucedido
        // Replica o fluxo completo do login.component.ts:
        // autenticar → obter userId → buscar contexto → salvar dados → carregar permissoes → redirecionar
        this.apiService.autenticar(this.emailUsuario, this.senha).subscribe({
          next: (response: any) => {
            const authHeader = response?.headers?.get?.('Authorization');
            if (authHeader) this.safe.setItem('authToken', authHeader);

            const userId = response?.body?.id;
            if (userId !== undefined && userId !== null) {
              this.safe.setItem('userId', String(userId));
            }

            if (!userId && userId !== 0) {
              // userId nao retornado, fallback para login manual
              this.carregandoCadastro = false;
              this.router.navigate(['/login']);
              return;
            }

            // Buscar contexto do usuario para obter empresaId
            this.usuarioService.getContexto(`/usuario/${userId}`).subscribe({
              next: (usuario: UsuarioContextoDTO) => {
                const empresaId = usuario?.empresaId;
                if (empresaId != null) {
                  this.safe.setItem('id', String(empresaId));
                }
                this.safe.setItem('email', usuario.email);
                if (usuario.categoriaAdmin != null) {
                  this.safe.setItem('categoriaAdmin', String(usuario.categoriaAdmin));
                }
                if (usuario.categoriaAcessoMaster != null) {
                  this.safe.setItem('categoriaAcessoMaster', String(usuario.categoriaAcessoMaster));
                }

                this.authService.setAuthenticated(true);

                // Carregar permissoes e redirecionar
                this.permissaoService.carregarPermissoes(usuario.email).subscribe({
                  next: () => {
                    this.carregandoCadastro = false;
                    const rota = this.permissaoService.obterPrimeiraRotaAcessivel();
                    this.router.navigateByUrl(rota);
                  },
                  error: () => {
                    this.carregandoCadastro = false;
                    this.router.navigateByUrl('/');
                  },
                });
              },
              error: () => {
                // Fallback: contexto falhou, redirecionar para login
                this.carregandoCadastro = false;
                this.router.navigate(['/login']);
              }
            });
          },
          error: () => {
            // Fallback: se o login automatico falhar, redirecionar para /login
            this.carregandoCadastro = false;
            this.router.navigate(['/login']);
          }
        });
      },
      error: (err: any) => {
        this.carregandoCadastro = false;
        console.error('Erro ao cadastrar', err);
        // Problema 8 - Exibir mensagem do backend se disponível
        const mensagemBackend = err?.error?.message || err?.error || null;
        const mensagemExibir = (mensagemBackend && typeof mensagemBackend === 'string')
          ? mensagemBackend
          : this.mensagens.erro_cadastrar;
        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: mensagemExibir,
        });
      }
    });
  }

  /**
   * Validacoes compartilhadas entre modo padrao e social
   */
  private validarCamposCompartilhados(): boolean {
    // Problema 5 - Validação de comprimento mínimo do nome (min 4 caracteres)
    if (this.nome.trim().length < 4) {
      this.messageService.add({
        severity: 'warn',
        summary: this.mensagens.atencao,
        detail: this.mensagens.nome_muito_curto,
      });
      return false;
    }

    // Problema 3 - Validação de formato de e-mail
    // `isEmailValido` ja aplica trim internamente.
    if (!isEmailValido(this.emailUsuario)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.mensagens.atencao,
        detail: this.mensagens.email_invalido,
      });
      return false;
    }

    // BUG-03: Limite corrigido de 20 para 15 (padrão E.164)
    const telefoneLimpo = this.telefone.replace(/\D/g, '');
    if (telefoneLimpo.length < 9 || telefoneLimpo.length > 15) {
      this.messageService.add({
        severity: 'warn',
        summary: this.mensagens.atencao,
        detail: this.mensagens.telefone_invalido,
      });
      return false;
    }

    return true;
  }

  /**
   * Cadastro social (Google) - sem senha
   */
  private cadastrarSocial(): void {
    const telefoneLimpo = this.telefone.replace(/\D/g, '');
    const formData: PrimeiroCadastroSocialDTO = {
      nome: this.nome,
      emailUsuario: this.emailUsuario,
      ddi: this.paisSelecionado?.ddi?.replace('+', '').trim() || '595',
      telefone: telefoneLimpo,
      atividadeDaEmpresa: this.atividadeDaEmpresa!,
      ruc: this.ruc.trim(),
      cidadeId: Number(this.cidadeId),
      moeda: this.moeda,
    };

    // Vinculo de indicacao — anexa apenas quando presente (igual ao cadastro normal).
    // O backend propaga estes campos do `primeiroCadastroDTO` aninhado nos dois
    // fluxos sociais (Google e Apple).
    if (this.tokenIndicacao) formData.tokenIndicacao = this.tokenIndicacao;
    if (this.codigoIndicacao) formData.codigoIndicacao = this.codigoIndicacao;
    if (this.canalIndicacao) formData.canalIndicacao = this.canalIndicacao;

    this.carregandoCadastro = true;

    const registro$ = this.modoSocial === 'google'
      ? this.socialLoginService.registrarComGoogle(this.socialData!.token, formData)
      : this.socialLoginService.registrarComApple(this.socialData!, formData);

    registro$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.mensagens.sucesso,
          detail: this.translate.instant('social_login.cadastro_social_sucesso'),
        });
        // Carregar permissoes antes de redirecionar (completarLogin ja salvou sessao)
        const email = this.safe.getItem('email') || this.emailUsuario;
        this.permissaoService.carregarPermissoes(email).subscribe({
          next: () => {
            this.carregandoCadastro = false;
            const rota = this.permissaoService.obterPrimeiraRotaAcessivel();
            this.router.navigateByUrl(rota);
          },
          error: () => {
            this.carregandoCadastro = false;
            this.router.navigateByUrl('/');
          },
        });
      },
      error: (err: any) => {
        this.carregandoCadastro = false;
        console.error('Erro ao cadastrar via social', err);
        const mensagemBackend = err?.error?.message || err?.error || null;
        const mensagemExibir = (mensagemBackend && typeof mensagemBackend === 'string')
          ? mensagemBackend
          : this.mensagens.erro_cadastrar;
        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: mensagemExibir,
        });
      },
    });
  }

  /**
   * Cadastrar com Google diretamente da tela de signup (modo padrao)
   */
  cadastrarComGoogle(): void {
    this.loadingGoogleSignup = true;
    this.socialLoginService.loginComGoogle().subscribe({
      next: (resultado) => {
        this.loadingGoogleSignup = false;
        if (resultado.tipo === 'login_ok') {
          this.messageService.add({
            severity: 'info',
            summary: this.mensagens.sucesso,
            detail: this.translate.instant('social_login.usuario_ja_cadastrado'),
          });
          const email = this.safe.getItem('email') || this.emailUsuario;
          this.permissaoService.carregarPermissoes(email).subscribe({
            next: () => this.router.navigateByUrl(this.permissaoService.obterPrimeiraRotaAcessivel()),
            error: () => this.router.navigateByUrl('/'),
          });
        } else if (resultado.tipo === 'novo_usuario') {
          this.modoSocial = resultado.dados.provedor;
          this.socialData = resultado.dados;
          this.emailUsuario = resultado.dados.email || '';
          this.nome = resultado.dados.nome || '';
        }
      },
      error: (err) => {
        this.loadingGoogleSignup = false;
        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: this.translate.instant('social_login.erro_google'),
        });
      },
    });
  }

  /**
   * Cadastrar com Apple diretamente da tela de signup (modo padrao)
   */
  cadastrarComApple(): void {
    this.loadingAppleSignup = true;
    this.socialLoginService.loginComApple().subscribe({
      next: (resultado) => {
        this.loadingAppleSignup = false;
        if (resultado.tipo === 'login_ok') {
          this.messageService.add({
            severity: 'info',
            summary: this.mensagens.sucesso,
            detail: this.translate.instant('social_login.usuario_ja_cadastrado'),
          });
          const email = this.safe.getItem('email') || this.emailUsuario;
          this.permissaoService.carregarPermissoes(email).subscribe({
            next: () => this.router.navigateByUrl(this.permissaoService.obterPrimeiraRotaAcessivel()),
            error: () => this.router.navigateByUrl('/'),
          });
        } else if (resultado.tipo === 'novo_usuario') {
          this.modoSocial = resultado.dados.provedor;
          this.socialData = resultado.dados;
          this.emailUsuario = resultado.dados.email || '';
          this.nome = resultado.dados.nome || '';
        }
      },
      error: (err) => {
        this.loadingAppleSignup = false;
        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: this.translate.instant('social_login.erro_apple'),
        });
      },
    });
  }
}
